<template>
  <div :class="props.supportsContent ? 'search-with-scope flex w-full items-stretch' : 'contents'">
    <!--
      v-if rather than a `hidden sm:flex` utility: PrimeVue's own `.p-select { display: inline-flex }`
      loads after Tailwind's utilities and has equal specificity, so `hidden` lost the tie and the
      select stayed on screen. v-if also keeps it out of the tab order when it is not offered.
    -->
    <Select
        v-if="props.supportsContent && scopePickerVisible"
        v-model="scope"
        :options="availableScopes"
        optionLabel="label"
        optionValue="value"
        class="search-scope-select shrink-0"
        aria-label="Search scope"
    />
    <AutoComplete
        v-bind="$attrs"
        ref="searchInputRef"
        v-model="searchString"
        :suggestions="filteredSuggestions"
        option-group-label="name"
        option-group-children="items"
        optionLabel="ticker"
        @complete="onSearch"
        @item-select="handleSelection"
        :placeholder="placeholderText"
        input-class="w-full placeholder:text-md placeholder:font-light"
        variant="filled"
        panel-class="shadow-xl shadow-black/20"
        scroll-height="50vh"
        pt:optiongroup:class="text-primary font-bold tracking-wide border-b mt-5 first:mt-0 mb-3"
        :class="props.supportsContent ? 'search-autocomplete-with-prefix flex-1 min-w-0' : ''"
    >
      <template #option="slotProps">
        <div class="flex justify-between w-full gap-2 items-center">
          <div class="text-wrap">
            <span>{{ slotProps.option.full_name }}</span>
            <span
                v-if="formattedPublishedDate( slotProps.option )"
                class="text-sm text-base-content/60"
            > - {{ formattedPublishedDate( slotProps.option ) }}</span>
          </div>
          <span
              v-if="slotProps.option.isStock || slotProps.option.isEtf || slotProps.option.isSector"
              class="font-semibold"
          >{{ slotProps.option.ticker }}</span>
        </div>
      </template>
    </AutoComplete>
  </div>
</template>

<script setup lang="ts">
// inheritAttrs: false so we can route fallthrough attrs (class, disabled, …)
// to the inner AutoComplete via v-bind="$attrs". When supportsContent is
// false the wrapper <div> uses display:contents so it has no layout effect.
defineOptions( { inheritAttrs: false } );

import { computed, ref } from "vue";
import { useRouter } from "vue-router";

import { useCompactViewport } from "@/composables/useCompactViewport";
import { storeToRefs } from "pinia";

import { useDebounceFn, useStorage } from "@vueuse/core";
import { useFuse } from "@vueuse/integrations/useFuse";
import { formatDateAsMonthDayYearString } from "@tmi/utils/date-time";

import Select from "primevue/select";
import { AutoCompleteOptionSelectEvent } from "primevue/autocomplete";

import { useTickersStore } from "@/stores/Tickers";
import { useSectorsStore } from "@/stores/Sectors";
import { useArticlesStore } from "@/stores/Articles";
import { useTradeAlertsStore } from "@/stores/TradeAlerts";
import { useWebinarsStore } from "@/stores/Webinars";
import { usePermissionsStore } from "@/stores/Permissions";
import { reportSearchEvent } from "@/composables/user-activity-monitor";
import routeManager from "@/router/route-manager";

import { IClickHandler } from "@tmi/types/handlers/click-handler";
import type {
  Article,
  ContentfulSector,
  SearchableTicker,
  TradeAlert,
  Webinar,
} from "@tmi/types/entities";

// ── Types ────────────────────────────────────────────────────────────────

type SearchScope = 'all' | 'tickers' | 'articles' | 'alerts' | 'webinars';

interface ScopeOption {
  label: string;
  value: SearchScope;
}

type SearchResult = {
  full_name: string;
  ticker: string;
  slug?: string;
  publishedDate?: string;
  isStock?: boolean;
  isEtf?: boolean;
  isSector?: boolean;
  isArticle?: boolean;
  isAlert?: boolean;
  isWebinar?: boolean;
};

type SearchGroupResult = {
  name: string;
  items: SearchResult[];
};

type FuseTickerItem = { item: SearchableTicker; refIndex: number };
type FuseSectorItem = { item: ContentfulSector; refIndex: number };
type FuseContentItem< T > = { item: T; refIndex: number };

// ── Props ────────────────────────────────────────────────────────────────

export interface ISearchProps {
  supportsStocks?: boolean;
  stockClickHandler?: IClickHandler;
  supportsEtfs?: boolean;
  etfClickHandler?: IClickHandler;
  supportsSectors?: boolean;
  sectorClickHandler?: IClickHandler;
  /**
   * Enable Articles / Trade Alerts / Webinars search and render the scope
   * dropdown. Off by default — the PortfolioSearch and WatchlistSearch
   * usages of this component stay ticker-only.
   */
  supportsContent?: boolean;
}

const props = withDefaults( defineProps< ISearchProps >(), {
  supportsStocks: true,
  supportsEtfs: true,
  supportsSectors: true,
  supportsContent: false,
} );

// ── Stores & permissions ─────────────────────────────────────────────────

const router = useRouter();
const tickersStore = useTickersStore();
const sectorsStore = useSectorsStore();
const articlesStore = useArticlesStore();
const tradeAlertsStore = useTradeAlertsStore();
const webinarsStore = useWebinarsStore();

const { allArticles } = storeToRefs( articlesStore );
const { allTradeAlerts } = storeToRefs( tradeAlertsStore );
const { allWebinars } = storeToRefs( webinarsStore );

const {
  hasEtfAccess,
  hasSectorAccess,
  hasStockAccess,
  hasArticleAccess,
  hasProductTradeAlertsAccess,
  hasWebinarAccess,
} = storeToRefs( usePermissionsStore() );

// ── Scope state ──────────────────────────────────────────────────────────

const scope = useStorage< SearchScope >( 'global-search-scope', 'all' );

/*
 * The scope <Select> is hidden below `sm` (see the template and the scoped styles): it is
 * shrink-0 with a 6.5rem floor, and the private header only affords the whole search component
 * ~119px on a 375px phone, which left the actual input 26px wide — 2px of usable text area once
 * padding is taken off. Hiding it hands that width back to the input.
 *
 * `scope` is persisted per user, so a scope chosen on desktop would otherwise silently narrow
 * search on a phone with no visible control to undo it. Fall back to 'all' whenever the picker
 * is off screen.
 */
const { isCompactViewport } = useCompactViewport();

/* The picker is offered from `sm` up — the inverse of the shared compact-viewport flag. */
const scopePickerVisible = computed< boolean >( () => !isCompactViewport.value );

const effectiveScope = computed< SearchScope >( () => scopePickerVisible.value ? scope.value : 'all' );

/*
 * The header affords the search component ~111px on a 375px phone, so the full
 * "Search tickers, articles, alerts, webinars…" string (300px) is truncated to a couple of
 * characters and reads as a rendering fault. Use the short label at the width where the long one
 * cannot land. `scopePickerVisible` is the same >=640px test, reused deliberately.
 */
const placeholderText = computed< string >( () => {
  if ( !props.supportsContent ) return 'Search ticker or name';
  return scopePickerVisible.value ? 'Search tickers, articles, alerts, webinars…' : 'Search';
} );

const availableScopes = computed< ScopeOption[] >( () => {
  const opts: ScopeOption[] = [ { label: 'All', value: 'all' } ];
  const anyTicker =
    ( props.supportsStocks && hasStockAccess.value ) ||
    ( props.supportsEtfs && hasEtfAccess.value ) ||
    ( props.supportsSectors && hasSectorAccess.value );
  if ( anyTicker ) opts.push( { label: 'Tickers', value: 'tickers' } );
  if ( hasArticleAccess.value ) opts.push( { label: 'Articles', value: 'articles' } );
  if ( hasProductTradeAlertsAccess.value ) opts.push( { label: 'Trade Alerts', value: 'alerts' } );
  if ( hasWebinarAccess.value ) opts.push( { label: 'Webinars', value: 'webinars' } );
  return opts;
} );

// ── Search state & Fuse configs ──────────────────────────────────────────

const searchInputRef = ref();
const searchString = ref< string >( '' );
const filteredSuggestions = ref< SearchGroupResult[] >( [] );

const tickerFuseConfig = {
  resultLimit: 10,
  fuseOptions: {
    shouldSort: true,
    ignoreLocation: true,
    isCaseSensitive: false,
    threshold: 0.3,
    keys: [
      { name: "name", weight: 0.3 },
      { name: "ticker", weight: 0.7 },
      { name: "symbol", weight: 0.7 },
    ],
  },
};

// Looser threshold for free-form content titles vs short ticker symbols.
const contentFuseConfig = {
  resultLimit: 10,
  fuseOptions: {
    shouldSort: true,
    ignoreLocation: true,
    isCaseSensitive: false,
    threshold: 0.4,
    keys: [
      { name: "title", weight: 0.85 },
      { name: "slug", weight: 0.15 },
    ],
  },
};

// ── Lazy hydration ───────────────────────────────────────────────────────

// Articles + Webinars are loaded at app boot. TradeAlerts is initialized
// on-demand by routes that need it; for the global search to find alerts
// without bloating boot we trigger the store's own initialize() lazily,
// once, on the user's first search interaction.
let alertsInitialized = false;

async function ensureAlertsInitialized() {
  if ( alertsInitialized || !props.supportsContent || !hasProductTradeAlertsAccess.value ) return;
  alertsInitialized = true;
  try {
    await tradeAlertsStore.initialize();
  } catch ( err: any ) {
    alertsInitialized = false; // allow retry on the next keystroke
    console.warn( 'global-search: failed to lazy-init trade alerts:', err );
  }
}

// ── Search ───────────────────────────────────────────────────────────────

const debouncedSearch = useDebounceFn( () => {
  filteredSuggestions.value.splice( 0 );

  const query: string = searchString.value.toLocaleLowerCase();

  // When the scope dropdown isn't rendered (supportsContent=false), every
  // ticker group is eligible — preserves the existing portfolio/watchlist
  // behavior verbatim.
  const tickerScopeActive = !props.supportsContent
      || effectiveScope.value === 'all'
      || effectiveScope.value === 'tickers';

  if ( tickerScopeActive && hasStockAccess.value && props.supportsStocks ) {
    const { results } = useFuse( query, tickersStore.searchableStocks, tickerFuseConfig );
    if ( results.value.length > 0 ) {
      filteredSuggestions.value.push( {
        name: 'Stocks',
        items: results.value.map( ( r: FuseTickerItem ): SearchResult => ( {
          full_name: r.item.name,
          ticker: r.item.ticker,
          isStock: true,
        } ) ),
      } );
    }
  }

  if ( tickerScopeActive && hasEtfAccess.value && props.supportsEtfs ) {
    const { results } = useFuse( query, tickersStore.searchableEtfs, tickerFuseConfig );
    if ( results.value.length > 0 ) {
      filteredSuggestions.value.push( {
        name: 'ETFs',
        items: results.value.map( ( r: FuseTickerItem ): SearchResult => ( {
          full_name: r.item.name,
          ticker: r.item.ticker,
          isEtf: true,
        } ) ),
      } );
    }
  }

  if ( tickerScopeActive && hasSectorAccess.value && props.supportsSectors ) {
    const { results } = useFuse( query, sectorsStore.allSectors, tickerFuseConfig );
    if ( results.value.length > 0 ) {
      filteredSuggestions.value.push( {
        name: 'Sectors',
        items: results.value.map( ( r: FuseSectorItem ): SearchResult => ( {
          full_name: r.item.name,
          ticker: r.item.symbol,
          isSector: true,
        } ) ),
      } );
    }
  }

  // Content types only run when the consumer opts into supportsContent.
  if ( props.supportsContent ) {
    const articlesScopeActive = effectiveScope.value === 'all' || effectiveScope.value === 'articles';
    if ( articlesScopeActive && hasArticleAccess.value ) {
      const { results } = useFuse( query, allArticles.value, contentFuseConfig );
      const items: SearchResult[] = results.value
          .filter( ( r: FuseContentItem< Article > ): boolean => !!r.item.slug && !!r.item.title )
          .map( ( r: FuseContentItem< Article > ): SearchResult => ( {
            full_name: r.item.title!,
            ticker: r.item.slug!,
            slug: r.item.slug!,
            publishedDate: r.item.publishedDate,
            isArticle: true,
          } ) );
      if ( items.length > 0 ) {
        filteredSuggestions.value.push( { name: 'Articles', items } );
      }
    }

    const alertsScopeActive = effectiveScope.value === 'all' || effectiveScope.value === 'alerts';
    if ( alertsScopeActive && hasProductTradeAlertsAccess.value ) {
      const { results } = useFuse( query, allTradeAlerts.value, contentFuseConfig );
      const items: SearchResult[] = results.value
          .filter( ( r: FuseContentItem< TradeAlert > ): boolean => !!r.item.slug && !!r.item.title )
          .map( ( r: FuseContentItem< TradeAlert > ): SearchResult => ( {
            full_name: r.item.title!,
            ticker: r.item.slug!,
            slug: r.item.slug!,
            publishedDate: r.item.publishedDate,
            isAlert: true,
          } ) );
      if ( items.length > 0 ) {
        filteredSuggestions.value.push( { name: 'Trade Alerts', items } );
      }
    }

    const webinarsScopeActive = effectiveScope.value === 'all' || effectiveScope.value === 'webinars';
    if ( webinarsScopeActive && hasWebinarAccess.value ) {
      const { results } = useFuse( query, allWebinars.value, contentFuseConfig );
      const items: SearchResult[] = results.value
          .filter( ( r: FuseContentItem< Webinar > ): boolean => !!r.item.slug && !!r.item.title )
          .map( ( r: FuseContentItem< Webinar > ): SearchResult => ( {
            full_name: r.item.title!,
            ticker: r.item.slug!,
            slug: r.item.slug!,
            publishedDate: r.item.publishedDate,
            isWebinar: true,
          } ) );
      if ( items.length > 0 ) {
        filteredSuggestions.value.push( { name: 'Webinars', items } );
      }
    }
  }

  searchInputRef.value.overlayVisible = true;
  searchInputRef.value.searching = false;
}, 350, { maxWait: 5000 } );

function onSearch() {
  if ( !searchString.value ) return;
  if ( props.supportsContent ) ensureAlertsInitialized();
  debouncedSearch();
}

function handleSelection( selectedItem: AutoCompleteOptionSelectEvent ) {
  searchString.value = '';
  const result = selectedItem.value as SearchResult;
  reportSearchEvent( result.ticker );
  navigateToSelectedItem( result );
}

function navigateToSelectedItem( item: SearchResult ) {
  if ( item.isStock ) {
    props.stockClickHandler?.( item.ticker );
  } else if ( item.isEtf ) {
    props.etfClickHandler?.( item.ticker );
  } else if ( item.isSector ) {
    props.sectorClickHandler?.( item.ticker );
  } else if ( item.isArticle && item.slug ) {
    router.push( routeManager.articleRouteBySlug( item.slug ) );
  } else if ( item.isAlert && item.slug ) {
    router.push( routeManager.tradeAlertRouteBySlug( item.slug ) );
  } else if ( item.isWebinar && item.slug ) {
    router.push( routeManager.webinarRouteBySlug( item.slug ) );
  }
}

function formattedPublishedDate( item: SearchResult ): string | null {
  if ( !( item.isArticle || item.isAlert || item.isWebinar ) || !item.publishedDate ) return null;
  const date = new Date( item.publishedDate );
  if ( Number.isNaN( date.getTime() ) ) return null;
  return formatDateAsMonthDayYearString( item.publishedDate );
}
</script>

<style scoped>
/*
  Everything that joins the scope <Select> and the <AutoComplete> into one pill is gated to
  >=640px, because below that the Select is hidden and the input stands alone.

  The gate matters for correctness, not just looks: `display: flex` here outweighs Tailwind's
  `.hidden` utility on specificity (the scoped attribute selector adds a class-level component),
  so an ungated rule would keep the Select on screen on phones. It also has to match
  SCOPE_PICKER_QUERY in the script block.
*/
@media (min-width: 640px) {
  .search-with-scope :deep( .p-select ) {
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
    border-right-width: 0;
    display: flex;
    align-items: center;
  }

  .search-with-scope :deep( .search-autocomplete-with-prefix .p-autocomplete-input ),
  .search-with-scope :deep( .search-autocomplete-with-prefix .p-autocomplete-fluid .p-autocomplete-input ) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  .search-with-scope :deep( .p-select-label ) {
    display: flex;
    align-items: center;
    line-height: 1.2;
    padding-right: 0.6rem;
  }

  .search-with-scope :deep( .p-select-dropdown ) {
    width: 2rem;
    min-width: 2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }

  .search-scope-select {
    min-width: 8.5rem;
  }
}
</style>
